import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import Cookies from 'universal-cookie';
import { MEETING_SCOPES, PEOPLE_SCOPES } from '@stores/authStore';
import { getScopesFromToken, hasRequiredScopes } from './authHelper';
import { analyticsUserType } from '../constants/userConstants';
import { shouldRecordSession } from './mixpanelSessionReplayHelper';
var cookies = new Cookies();
// record_console must stay false (the SDK default is true and our catch blocks log user/tenant data).
// Never add record_mask_text_selector without record_mask_all_text: the recorder then unmasks all text.
// record_sessions_percent is left at 0 on purpose; recording starts after identify (mixpanelIdentifyUser).
var SESSION_REPLAY_INIT_OPTIONS = {
    record_mask_all_text: true,
    record_mask_all_inputs: true,
    record_console: false,
    record_network: false,
    record_canvas: false,
    record_collect_fonts: false
};
var initMixpanel = function initMixpanel() {
    try {
        mixpanel.init(process.env.MIXPANEL_PROJECT_TOKEN, _object_spread({
            track_pageview: false,
            debug: process.env.NODE_ENV === 'development',
            persistence: 'localStorage'
        }, SESSION_REPLAY_INIT_OPTIONS));
    } catch (err) {
        console.log(err);
    }
};
var mixpanelAliasUser = function mixpanelAliasUser(user) {
    try {
        if (user) {
            mixpanel.alias(user._id);
        }
    } catch (err) {
        console.log(err);
    }
};
var mixpanelStartSessionRecordingIfEligible = function mixpanelStartSessionRecordingIfEligible(user) {
    if (!shouldRecordSession(user)) return;
    mixpanel.start_session_recording();
};
var mixpanelStopSessionRecording = function mixpanelStopSessionRecording() {
    try {
        mixpanel.stop_session_recording();
    } catch (err) {
        console.log(err);
    }
};
var mixpanelIdentifyUser = function mixpanelIdentifyUser(user, inTeams) {
    var region = cookies.get('x-region') || localStorage.getItem('x-region');
    try {
        if (user) {
            var _user_tenant_trialSubscription, _user_tenant;
            var scopes = getScopesFromToken(user === null || user === void 0 ? void 0 : user.graphAccessToken);
            mixpanel.identify(user._id);
            var mixPanelRegister = {
                userID: user._id,
                role: user.role,
                subscriptionStatus: user.tenant.subscriptionStatus,
                dayFromSignupDate: moment().diff(moment(user.createdAt), 'days'),
                platform: inTeams ? 'teams' : 'web',
                environment: process.env.ENVIRONMENT,
                'Tenant Graph Size': user.tenant.graphTotalUserCount,
                'Tenant license count': user.tenant.licensedUserCount,
                'Tenant Name': user.tenant.Id,
                'Is Manager': user.isManager,
                goalsActive: !user.tenant.settings.goalDisabled,
                reviewsActive: !user.tenant.settings.reviewDisabled,
                surveysActive: !user.tenant.settings.surveyDisabled,
                recognitionsActive: !user.tenant.settings.recognitionDisabled,
                feedbacksActive: !user.tenant.settings.feedbackDisabled,
                tasksActive: !user.tenant.settings.taskDisabled,
                meetingsActive: !user.tenant.settings.meetingDisabled,
                goalApprovalActive: !!user.tenant.settings.enableGoalApprovalFlow,
                region: region,
                viewportW: window.innerWidth,
                viewportH: window.innerHeight,
                'Subscription License Type': user.tenant.subscriptionLicenseType || '',
                'Trial Subscription License Type': ((_user_tenant = user.tenant) === null || _user_tenant === void 0 ? void 0 : (_user_tenant_trialSubscription = _user_tenant.trialSubscription) === null || _user_tenant_trialSubscription === void 0 ? void 0 : _user_tenant_trialSubscription.isExpired) === false ? user.tenant.trialSubscription.licenseType || '' : '',
                'Tenant Created At': user.tenant.createdAt,
                'User Created At': user.createdAt,
                'Kacinci User': user.signUpRank,
                readAllUsersPermission: hasRequiredScopes(scopes, PEOPLE_SCOPES),
                calendarpermission: hasRequiredScopes(scopes, MEETING_SCOPES),
                user_type: analyticsUserType(user.userType)
            };
            mixpanel.register(mixPanelRegister);
            // Must run after identify + register so the replay is tied to the user's distinct_id.
            mixpanelStartSessionRecordingIfEligible(user);
        }
    } catch (err) {
        console.log(err);
    }
};
// Experiment tags (exp_<experimentKey>: variant) — registered as super properties (ride every
// event) AND set on the user profile; stale keys (experiments the user dropped out of) are
// unregistered so ghost variants don't linger in localStorage-persisted super properties.
// Called from experimentAnalyticsHelper, which owns the tag shape.
var mixpanelSyncExperimentTags = function mixpanelSyncExperimentTags(tags) {
    var staleTagKeys = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [];
    try {
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = staleTagKeys[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var key = _step.value;
                mixpanel.unregister(key);
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
        if (staleTagKeys.length > 0) {
            mixpanel.people.unset(staleTagKeys);
        }
        if (Object.keys(tags).length > 0) {
            mixpanel.register(tags);
            mixpanel.people.set(tags);
        }
    } catch (err) {
        console.log(err);
    }
};
// Mixpanel Experiments (Exposure Events collection method) reads exactly this event + property
// pair. The experiment key and variant key must match the experiment configured in the Mixpanel UI
// verbatim. Sent once per user per experiment (dedupe lives in experimentExposureHelper); the
// exp_<key> super properties above stay as they are and ride on this event too.
var MIXPANEL_EXPERIMENT_STARTED_EVENT = '$experiment_started';
// True when Mixpanel queued or sent the event; track() drops silently (opt-out, hooks) by returning falsy.
var mixpanelExperimentStartedEvent = function mixpanelExperimentStartedEvent(experimentKey, variant) {
    try {
        return Boolean(mixpanel.track(MIXPANEL_EXPERIMENT_STARTED_EVENT, {
            'Experiment name': experimentKey,
            'Variant name': variant
        }));
    } catch (err) {
        console.log(err);
        return false;
    }
};
var mixpanelSetUserProperty = function mixpanelSetUserProperty(key, value) {
    try {
        mixpanel.people.set(_define_property({}, key, value));
    } catch (err) {
        console.log(err);
    }
};
var mixpanelPageViewEvent = function mixpanelPageViewEvent(page, properties) {
    mixpanel.track_pageview(_object_spread({
        page: page
    }, properties));
};
var mixpanelButtonClickedEvent = function mixpanelButtonClickedEvent(buttonName, openedFrom, properties) {
    try {
        var urlName = window.location.href.split('#').pop();
        mixpanel.track('Button Clicked', _object_spread({
            buttonName: buttonName,
            openedFrom: openedFrom,
            pageUrl: urlName,
            projectType: 'app'
        }, properties));
    } catch (err) {
        console.log(err);
    }
};
/**
 * @param buttonName
 * @param openedFrom
 * @param properties
 * @returns Promise that resolves to true if the event is sent successfully, false otherwise but never rejects
 */ var mixpanelButtonClickedEventPromise = function mixpanelButtonClickedEventPromise(buttonName, openedFrom, properties) {
    var timeoutMs = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : 1000;
    return new Promise(function(resolve) {
        var settled = false;
        var done = function done(ok) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(ok);
        };
        var timer = setTimeout(function() {
            return done(false);
        }, timeoutMs);
        try {
            var urlName = typeof window !== 'undefined' ? window.location.href.split('#').pop() : undefined;
            mixpanel.track('Button Clicked', _object_spread({
                buttonName: buttonName,
                openedFrom: openedFrom,
                pageUrl: urlName,
                projectType: 'app'
            }, properties), {
                send_immediately: true,
                transport: 'sendBeacon'
            }, function() {
                done(true);
            });
        } catch (err) {
            console.log(err);
            done(false);
        }
    });
};
var mixpanelSubmitClickedEvent = function mixpanelSubmitClickedEvent(buttonName, openedFrom, properties) {
    try {
        var urlName = window.location.href.split('#').pop();
        mixpanel.track('Submit Clicked', _object_spread({
            buttonName: buttonName,
            openedFrom: openedFrom,
            pageUrl: urlName,
            projectType: 'app'
        }, properties));
    } catch (err) {
        console.log(err);
    }
};
var mixpanelOpenClickedEvent = function mixpanelOpenClickedEvent(buttonName, openedFrom, properties) {
    try {
        var urlName = window.location.href.split('#').pop();
        mixpanel.track('Open Clicked', _object_spread({
            buttonName: buttonName,
            openedFrom: openedFrom,
            pageUrl: urlName,
            projectType: 'app'
        }, properties));
    } catch (err) {
        console.log(err);
    }
};
var mixpanelLogoutEvent = function mixpanelLogoutEvent() {
    try {
        mixpanel.track('Logout');
        mixpanelStopSessionRecording();
        resetMixpanel();
    } catch (err) {
        console.log(err);
    }
};
var resetMixpanel = function resetMixpanel() {
    mixpanel.reset();
};
var mixpanelCustomEvent = function mixpanelCustomEvent(eventName) {
    var properties = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    try {
        mixpanel.track(eventName, _object_spread({
            projectType: 'app'
        }, properties));
    } catch (err) {
        console.log(err);
    }
};
var mixpanelErrorEvent = function mixpanelErrorEvent(error, page, properties) {
    try {
        mixpanel.track('Error', _object_spread({
            error: error,
            page: page,
            projectType: 'app'
        }, properties));
    } catch (err) {
        console.log(err);
    }
};
export { initMixpanel, resetMixpanel, mixpanelButtonClickedEvent, mixpanelButtonClickedEventPromise, mixpanelSubmitClickedEvent, mixpanelOpenClickedEvent, mixpanelAliasUser, mixpanelIdentifyUser, mixpanelLogoutEvent, mixpanelStopSessionRecording, mixpanelPageViewEvent, mixpanelCustomEvent, mixpanelErrorEvent, mixpanelSetUserProperty, mixpanelSyncExperimentTags, mixpanelExperimentStartedEvent };
